'use client';

import * as React from 'react';

// Self-heal the "Application error: a client-side exception has occurred" dead
// screen that strands the (always-open, often-backgrounded) desktop app after a
// long disconnect. The usual cause is a deploy that happened while the client
// sat idle: the stale page then lazy-loads a JS chunk whose hash no longer
// exists, the dynamic import rejects with a ChunkLoadError, and because that
// failure is outside React's render path none of the error.tsx boundaries can
// catch it. A full reload pulls the fresh build manifest and fixes it.
//
// Guarded against reload loops via sessionStorage: we reload at most once per
// short window, so a genuinely broken build degrades to the static error rather
// than thrashing. Mounted in the root layout so it is always listening.

const RELOAD_FLAG = 'staffy:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 20_000;

function isChunkError(message: string): boolean {
  return /Loading chunk [^ ]+ failed|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
    message,
  );
}

export function ChunkReloadGuard(): null {
  React.useEffect(() => {
    function reloadOnce(): void {
      try {
        const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? '0');
        if (Date.now() - last < RELOAD_COOLDOWN_MS) return; // already tried; avoid loop
        sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
      } catch {
        // sessionStorage unavailable (private mode): fall through to reload.
      }
      window.location.reload();
    }

    function onError(ev: ErrorEvent): void {
      const message = ev.message || ev.error?.message || '';
      if (isChunkError(String(message))) {
        ev.preventDefault();
        reloadOnce();
      }
    }
    function onRejection(ev: PromiseRejectionEvent): void {
      const reason = ev.reason;
      const message =
        reason instanceof Error ? reason.message : String(reason ?? '');
      if (isChunkError(message)) {
        reloadOnce();
      }
    }

    window.addEventListener('error', onError, true);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
